import { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import { dataGridPremiumColumns } from '../../../utils/helper';
import useGenerateJokerCols from '../../../hooks/useGenerateJokerCols';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  AutoCompleteSelectServiceUI,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
  ButtonUI,
} from '../../Interface';
import { formatNumber } from '../../../utils/currencyHelper';
import { IssueMethod, IssueMethodList, isAutomaticIssueMethodAllowed } from '../../../config/production';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { bignumber, divide, multiply, round } from 'mathjs';
import useParam from '../../../context/ParamContext';
import ItemStockDialog from '../../Item/ItemStockDialog';
import masterDataService from '../../../service/masterData.service';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../../utils/itemAutocomplete';

const normalizeSingleItemResponse = (response) => {
  if (Array.isArray(response?.body)) {
    return response.body[0] ?? null;
  }

  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  return response?.body ?? response ?? null;
};

const ItemStockInfo = (props) => {
  const { getItemMetaByCode, setItemStockDialogState, disabled, line } = props;

  const itemCode = line?.itemCode ?? null;
  const isStockBtnDisabled = !itemCode;

  return (
    <Tooltip title={isStockBtnDisabled ? 'Előbb adj meg cikkszámot' : 'Készlet információ'}>
      <span>
        <IconButton
          aria-label="Készlet információ"
          size="small"
          disabled={isStockBtnDisabled || disabled}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!itemCode) return;

            const itemFromAutocomplete = await getItemMetaByCode(itemCode);

            setItemStockDialogState({
              open: true,
              data: itemFromAutocomplete,
            });
          }}
        >
          <Inventory2OutlinedIcon fontSize="inherit" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const EditToolbar = (props) => {
  const { params } = useParam();
  const { disabled, push, defaultItem, getItemMetaByCode, apiRef } = props;
  const { values, validateField, setFieldValue } = useFormikContext();

  const validateLayerRatios = () => {
    const itemsWithLayer = values.productionItem.filter(
      (item) =>
        item.joker_char1 !== null &&
        item.joker_char1 !== undefined &&
        item.joker_char1 !== '' &&
        item.joker_num1 !== null &&
        item.joker_num1 !== undefined &&
        item.joker_num1 !== '' &&
        item.joker_num2 !== null &&
        item.joker_num2 !== undefined &&
        item.joker_num2 !== ''
    );

    if (itemsWithLayer.length === 0) return;

    const layerGroups = {};

    for (const item of itemsWithLayer) {
      const layer = item.joker_char1;
      if (!layerGroups[layer]) layerGroups[layer] = [];
      layerGroups[layer].push(item);
    }

    const layerRatioValues = [];

    for (const [layer, items] of Object.entries(layerGroups)) {
      const firstRatio = items[0].joker_num1;
      const allEqual = items.every((i) => i.joker_num1 === firstRatio);

      if (!allEqual) {
        addNotification({
          content: `Hibás rétegarányok a(z) "${layer}" rétegen belül!`,
          type: NotificationType.ERROR,
        });
        return;
      }

      layerRatioValues.push(firstRatio);
    }

    const totalRatio = layerRatioValues.reduce((sum, val) => sum + Number(val || 0), 0);
    if (totalRatio !== 100) {
      addNotification({
        content: `A rétegarányok összege nem 100%!`,
        type: NotificationType.ERROR,
      });
      return;
    }

    return layerGroups;
  };

  const validateLayerDistributions = (layerGroups) => {
    for (const [layer, items] of Object.entries(layerGroups)) {
      const sum = items.reduce((acc, item) => acc + Number(item.joker_num2 || 0), 0);
      if (sum !== 100) {
        addNotification({
          content: `A(z) "${layer}" réteg arányai nem adják ki a 100%-ot!`,
          type: NotificationType.ERROR,
        });
        return true;
      }
    }
    return false;
  };

  const calculateQuantitiesFromRatios = async () => {
    const prodQty = values.productionQuantity;
    const prodWeight = values.weightPerItem;

    if (!prodQty || !prodWeight) {
      addNotification({
        content: `Hiányzó mennyiségek!`,
        type: NotificationType.ERROR,
      });
      return;
    }

    for (let index = 0; index < values.productionItem.length; index += 1) {
      const item = values.productionItem[index];
      const jokerRatio = item.joker_num1;
      const layerDistribution = item.joker_num2;

      const selectedItem = await getItemMetaByCode(item.itemCode);
      const itemWeight = selectedItem?.weight;

      if (!itemWeight) {
        addNotification({
          content: `Hiányzó súly a cikkben! (${selectedItem.itemCode})`,
          type: NotificationType.ERROR,
        });
        return false;
      }

      const result = divide(
        multiply(
          divide(multiply(bignumber(prodQty), bignumber(prodWeight)), divide(100, bignumber(jokerRatio))),
          divide(bignumber(layerDistribution), 100)
        ),
        bignumber(itemWeight)
      );

      const rounded = round(result, 6);
      setFieldValue(`productionItem.${index}.quantity`, rounded.toString());
      apiRef.current.setEditCellValue({
        id: item.id,
        field: 'quantity',
        value: rounded.toString(),
      });
    }

    return true;
  };

  const handleClick = async () => {
    defaultItem.id = uuidv4();
    defaultItem.quantityUnitId = null;
    await push(defaultItem);

    const fieldsToValidate = [
      `productionItem.${values.productionItem.length ?? 0}.itemCode`,
      `productionItem.${values.productionItem.length ?? 0}.quantity`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  const handleCalculateClick = async () => {
    const layerGroups = validateLayerRatios();
    if (!layerGroups) return;
    const distributionError = validateLayerDistributions(layerGroups);
    if (distributionError) return;
    await calculateQuantitiesFromRatios();
  };

  return (
    <GridToolbarContainer>
      <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
        <AddButtonUi disabled={disabled} onClick={handleClick} />
        {params.PRODUCTION_SHOW_COUNT_BTN === 'Y' && (
          <ButtonUI
            text="Számolás"
            size="sm"
            color="blue-gray"
            variant="gradient"
            className="mb-1.5"
            disabled={!values.productionItem?.length}
            onClick={handleCalculateClick}
          />
        )}
      </Box>
    </GridToolbarContainer>
  );
};

const ProductionItem = ({
  disabled,
  defaultItem,
  autocompleteOptions,
  calcItemBaseRate,
  autocompleteData,
  jokerLineFields,
}) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [lastChangedIndex, setLastChangedIndex] = useState(null);

  const [itemStockDialogState, setItemStockDialogState] = useState({ open: false, data: null });

  const handleItemStockDialogClose = () => {
    setItemStockDialogState({ open: false, data: null });
  };

  const getItemMetaByCode = async (itemCode) => {
    if (!itemCode) {
      return null;
    }

    const itemFromRows = (values.productionItem ?? []).find((item) => item?.itemCode === itemCode)?.item;
    if (itemFromRows) {
      return itemFromRows;
    }

    try {
      const response = await masterDataService.itemsFromTableToFormByItemCode(itemCode);
      const item = normalizeSingleItemResponse(response);

      if (!item) {
        return null;
      }

      return item;
    } catch {
      return null;
    }
  };

  const getItemByCode = (itemCode) => {
    if (!itemCode) {
      return null;
    }

    return (values.productionItem ?? []).find((item) => item?.itemCode === itemCode)?.item ?? null;
  };

  useEffect(() => {
    if (lastChangedIndex !== null) {
      calcItemBaseRate(lastChangedIndex);
    }
  }, [
    values?.productionItem?.[lastChangedIndex]?.quantity,
    values?.productionItem?.[lastChangedIndex]?.itemCode,
    lastChangedIndex,
  ]);

  const getValueIndex = (values, id) => {
    return values.productionItem.findIndex((rc) => rc.id === id);
  };

  const jokerColumns = useGenerateJokerCols(jokerLineFields, 'productionItem', getValueIndex);

  const notifyAutomaticIssueMethodRestricted = () => {
    addNotification({
      content: 'Automata kiadás csak Nincs készletkezelés módú cikknél állítható be.',
      type: NotificationType.WARNING,
    });
  };

  const getIssueMethodOptions = (itemCode) => {
    const item = getItemByCode(itemCode);

    if (!item || isAutomaticIssueMethodAllowed(item)) {
      return IssueMethodList;
    }

    return IssueMethodList.filter((option) => option.value !== IssueMethod.BACKFLUSH);
  };

  const handleIssueMethodChange = (valueIndex, issueMethod) => {
    const itemCode = values.productionItem?.[valueIndex]?.itemCode;
    const item = getItemByCode(itemCode);

    if (issueMethod === IssueMethod.BACKFLUSH && item && !isAutomaticIssueMethodAllowed(item)) {
      notifyAutomaticIssueMethodRestricted();
      return false;
    }

    setFieldValue(`productionItem.${valueIndex}.issueMethod`, issueMethod);
    return true;
  };

  const calculateItemWeight = (valueIndex, quantity, weightPerItem) => {
    const normalizedQuantity = Number(quantity);
    const normalizedWeightPerItem = Number(weightPerItem);

    if (!Number.isFinite(normalizedQuantity) || !Number.isFinite(normalizedWeightPerItem)) {
      setFieldValue(`productionItem.${valueIndex}.weight`, 0);
      return;
    }

    setFieldValue(
      `productionItem.${valueIndex}.weight`,
      round(multiply(normalizedQuantity, normalizedWeightPerItem), 2)
    );
  };

  const handleItemChange = async (valueIndex, selectedOption) => {
    const itemCode = selectedOption?.value ?? null;
    const findedItem = await getItemMetaByCode(itemCode);

    setFieldValue(`productionItem.${valueIndex}.itemCode`, itemCode);
    setFieldValue(`productionItem.${valueIndex}.item`, findedItem ?? null);
    setFieldValue(`productionItem.${valueIndex}.quantityUnitId`, findedItem?.quantityUnitId ?? null);
    setFieldValue(`productionItem.${valueIndex}.weightPerItem`, findedItem?.weight ?? null);
    calculateItemWeight(valueIndex, values.productionItem?.[valueIndex]?.quantity, findedItem?.weight ?? null);

    if (
      values.productionItem?.[valueIndex]?.issueMethod === IssueMethod.BACKFLUSH &&
      findedItem &&
      !isAutomaticIssueMethodAllowed(findedItem)
    ) {
      setFieldValue(`productionItem.${valueIndex}.issueMethod`, IssueMethod.MANUAL);
      notifyAutomaticIssueMethodRestricted();
    }
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'itemCode',
        headerName: 'Cikk',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const itemCode = values.productionItem?.[valueIndex]?.itemCode;
          const itemMeta = getItemByCode(itemCode);

          return [itemMeta?.itemCode, itemMeta?.itemName].filter(Boolean).join(' - ') || itemCode || '';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const itemCode = values.productionItem?.[valueIndex]?.itemCode;
          const itemMeta = getItemByCode(itemCode);
          return (
            <Field
              name={`productionItem.${valueIndex}.itemCode`}
              type="text"
              variant="standard"
              as={AutoCompleteSelectServiceUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.productionItem?.[valueIndex]?.itemCode} />}
              onChange={(_e, newVal) => {
                const val = newVal?.value ?? null;
                void handleItemChange(valueIndex, newVal);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: val,
                });
                setLastChangedIndex(valueIndex);
              }}
              onBlur={async () => await validateField(`productionItem.${valueIndex}.itemCode`)}
              selectedValue={values.productionItem?.[valueIndex]?.itemCode}
              selectedLabelValue={
                [itemMeta?.itemCode, itemMeta?.itemName].filter(Boolean).join(' - ') || itemCode || ''
              }
              service={itemAutocompleteService}
              serviceRequestBuilder={itemAutocompleteRequestBuilder}
              searchModeEnabled
            />
          );
        },
      },
      {
        field: 'baseRate',
        headerName: 'Alaphányad',
        type: 'string',
        sortable: false,
        renderCell: (data) => formatNumber(data.row.baseRate),
      },
      {
        field: 'quantity',
        headerName: 'Mennyiség',
        type: 'string',
        editable: true,
        sortable: false,
        valueGetter: (data, row) => {
          const valueIndex = getValueIndex(values, row.id);
          return values?.productionItem?.[valueIndex]?.quantity;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`productionItem.${valueIndex}.quantity`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.productionItem?.[valueIndex]?.quantity} />}
              onChange={(e) => {
                setFieldValue(`productionItem.${valueIndex}.quantity`, e.target.value ?? '');
                calculateItemWeight(valueIndex, e.target.value, values.productionItem?.[valueIndex]?.weightPerItem);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
                setLastChangedIndex(valueIndex);
              }}
              onBlur={async () => await validateField(`productionItem.${valueIndex}.quantity`)}
            />
          );
        },
      },
      {
        field: 'quantityUnitId',
        headerName: 'Me.',
        type: 'string',
        filterable: false,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          const findedUnit = autocompleteData.quantityUnit.find(
            (u) => u.id === values.productionItem?.[valueIndex]?.quantityUnitId
          );
          return findedUnit?.code ?? '';
        },
      },
      {
        field: 'weightPerItem',
        headerName: 'Súly / Tétel',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.productionItem?.[valueIndex]?.weightPerItem;
        },
      },
      {
        field: 'weight',
        headerName: 'Súly',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.productionItem?.[valueIndex]?.weight;
        },
      },
      {
        field: 'description',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`productionItem.${valueIndex}.description`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.productionItem?.[valueIndex]?.description} />}
              onChange={(e) => {
                setFieldValue(`productionItem.${valueIndex}.description`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`productionItem.${valueIndex}.description`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },

      {
        field: 'secondaryProduct',
        headerName: 'Melléktermék',
        type: 'boolean',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`productionItem.${valueIndex}.secondaryProduct`}
              type="checkbox"
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.productionItem?.[valueIndex]?.secondaryProduct} />}
              checked={values.productionItem?.[valueIndex]?.secondaryProduct ?? false}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`productionItem.${valueIndex}.secondaryProduct`, e.target.checked);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
            />
          );
        },
      },

      {
        field: 'whsCode',
        headerName: 'Raktár',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autocompleteOptions?.warehouse.find((u) => {
            return u.value === values.productionItem?.[valueIndex]?.whsCode;
          });
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
              <Typography sx={{ fontSize: '14px' }}>{findedItem?.label}</Typography>

              {values.productionItem?.[valueIndex]?.whsCode && (
                <ItemStockInfo
                  disabled={disabled}
                  setItemStockDialogState={setItemStockDialogState}
                  getItemMetaByCode={getItemMetaByCode}
                  line={values.productionItem?.[valueIndex] ?? data.row}
                />
              )}
            </Box>
          );
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autocompleteOptions?.warehouse.find((u) => {
            return u.value === values.productionItem?.[valueIndex]?.whsCode;
          });
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Field
                  name={`productionItem.${valueIndex}.whsCode`}
                  as={AutoCompleteSelectUI}
                  helperText={<FormErrorUI message={errors?.productionItem?.[valueIndex]?.whsCode} />}
                  selectedValue={values.productionItem?.[valueIndex]?.whsCode}
                  value={findedItem}
                  onChange={(_e, newVal) => {
                    setFieldValue(`productionItem.${valueIndex}.whsCode`, newVal?.value ?? null);
                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: newVal?.value ?? null,
                    });
                  }}
                  dataset={autocompleteOptions?.warehouse}
                  onBlur={async () => await validateField(`productionItem.${valueIndex}.whsCode`)}
                />
              </Box>

              {values.productionItem?.[valueIndex]?.whsCode && (
                <ItemStockInfo
                  setItemStockDialogState={setItemStockDialogState}
                  getItemMetaByCode={getItemMetaByCode}
                  line={values.productionItem?.[valueIndex] ?? data.row}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: 'issueMethod',
        headerName: 'Kiadás módja',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = IssueMethodList.find((u) => u.value === values.productionItem?.[valueIndex]?.issueMethod);
          return findedItem?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = IssueMethodList.find((u) => u.value === values.productionItem?.[valueIndex]?.issueMethod);
          const issueMethodOptions = getIssueMethodOptions(values.productionItem?.[valueIndex]?.itemCode);
          return (
            <Field
              name={`productionItem.${valueIndex}.issueMethod`}
              as={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.productionItem?.[valueIndex]?.issueMethod} />}
              selectedValue={values.productionItem?.[valueIndex]?.issueMethod}
              value={findedItem}
              onChange={(_e, newVal) => {
                const isChanged = handleIssueMethodChange(valueIndex, newVal?.value ?? null);
                if (!isChanged) return;

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              dataset={issueMethodOptions}
              onBlur={async () => await validateField(`productionItem.${valueIndex}.issueMethod`)}
            />
          );
        },
      },

      {
        field: 'isseudQuantity',
        headerName: 'Kiadott mennyiség',
        type: 'number',
      },
      ...jokerColumns,
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="productionItem">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ProductionItem}
                height={480}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.productionItem ?? []}
                editMode="row"
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultItem,
                    getItemMetaByCode,
                    apiRef,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                  for (const rowId of rowsInEditMode) {
                    if (params.row.id.toString() !== rowId) {
                      apiRef.current.stopRowEditMode({ id: rowId });
                    }
                  }

                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
      <ItemStockDialog
        open={itemStockDialogState.open}
        data={itemStockDialogState.data}
        onClose={handleItemStockDialogClose}
        showPrice={false}
      />
    </div>
  );
};

export default ProductionItem;
